import React from 'react';
import { Box, Divider, Stack, Tab, Tabs, Typography, IconButton, Alert } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

export interface SplitTabItem {
  key: string;
  label: React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactNode;
  /** When true, tab cannot be selected (MUI Tab disabled). */
  disabled?: boolean;
  /** Native title while disabled (keeps Tab as Tabs direct child). */
  disabledTooltip?: string;
}

export interface SplitTabsPanelProps {
  tabs: SplitTabItem[];
  defaultKey?: string;
  selectedKey?: string;
  onChange?: (key: string) => void;
  height?: number | string;
  headerRight?: React.ReactNode;
  /** Right sticky header leading slot (e.g. PL Period Tabs); same row as headerRight + maximize */
  headerLeft?: React.ReactNode;
  allowMaximize?: boolean;
  /** If true, render all tab contents (non-selected are hidden via CSS) */
  renderAll?: boolean;
  /**
   * With renderAll: keep inactive panes in the layout (visibility:hidden) instead of display:none.
   * Needed for virtualized tables (e.g. FsDetail PL) so row windows stay measured across tab switches.
   */
  preserveInactivePaneLayout?: boolean;
  errorMsg?: string | null;
  warningMsg?: string | null;
  sx?: SxProps<Theme>;
  leftPaneSx?: SxProps<Theme>;
  rightPaneSx?: SxProps<Theme>;
  contentWrapperSx?: SxProps<Theme>;
}

type SplitTabsTableOverflowApi = {
  /** Report a TableContainer node; panel normalizes overflow against full left-pane width. */
  report: (id: string, el: HTMLElement) => void;
  unregister: (id: string) => void;
};

const SplitTabsTableOverflowContext = React.createContext<SplitTabsTableOverflowApi | null>(null);

/** True when SplitTabsPanel is in auto/manual compact UI (icon tabs + headerLeft shrink). */
const SplitTabsHeaderCompactContext = React.createContext<boolean>(false);

/** Full vs icon left-pane widths — shared by overflow normalization. */
const LEFT_PANE_FULL_WIDTH = 200;
const LEFT_PANE_ICON_WIDTH = 64;

/**
 * For headerLeft content rendered inside SplitTabsPanel (e.g. PL period tabs).
 * True → render shrink mode; false → full content.
 */
export function useSplitTabsHeaderCompact(): boolean {
  return React.useContext(SplitTabsHeaderCompactContext);
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  (ref as React.MutableRefObject<T | null>).current = value;
}

/**
 * Attach to BalanceSheet / ProfitLoss (or PL multi) TableContainer.
 * Reports horizontal overflow (scrollWidth > clientWidth) to SplitTabsPanel; no-op outside the panel.
 */
export function useSplitTabsTableContainerRef<T extends HTMLElement = HTMLDivElement>(
  extraRef?: React.Ref<T>,
): React.RefCallback<T> {
  const api = React.useContext(SplitTabsTableOverflowContext);
  const id = React.useId();
  const roRef = React.useRef<ResizeObserver | null>(null);
  const nodeRef = React.useRef<T | null>(null);

  const sync = React.useCallback(() => {
    if (!api) return;
    const el = nodeRef.current;
    if (!el) return;
    api.report(id, el);
  }, [api, id]);

  React.useEffect(() => () => {
    roRef.current?.disconnect();
    roRef.current = null;
    api?.unregister(id);
  }, [api, id]);

  return React.useCallback((node: T | null) => {
    assignRef(extraRef, node);
    roRef.current?.disconnect();
    roRef.current = null;
    nodeRef.current = node;
    if (!api) return;
    if (!node) {
      api.unregister(id);
      return;
    }
    sync();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => sync());
    ro.observe(node);
    const table = node.querySelector('table');
    if (table) ro.observe(table);
    roRef.current = ro;
  }, [api, extraRef, id, sync]);
}

/**
 * SplitTabsPanel
 * - Left: vertical tabs
 * - Right: content of selected tab
 * - Icon vs full label: BS/PL table overflow (if any reporter) else headerRight slot width
 */
export function SplitTabsPanel({
  tabs,
  defaultKey,
  selectedKey,
  onChange,
  height = '40vh',
  headerRight,
  headerLeft,
  allowMaximize = true,
  renderAll = false,
  preserveInactivePaneLayout = false,
  errorMsg,
  warningMsg,
  sx,
  leftPaneSx,
  rightPaneSx,
  contentWrapperSx,
}: SplitTabsPanelProps) {
  const firstKey = tabs[0]?.key;
  const [innerKey, setInnerKey] = React.useState<string>(defaultKey || firstKey || '');
  const [collapsed, setCollapsed] = React.useState<boolean>(false);
  /** User clicked expand while auto-compact: keep full-name tabs until overflow clears. */
  const [forceExpanded, setForceExpanded] = React.useState(false);
  const [maximized, setMaximized] = React.useState<boolean>(false);
  /** True when headerRight slot is narrower than headerRight content's first measured width. */
  const [compactByHeaderRight, setCompactByHeaderRight] = React.useState(false);
  /** Per TableContainer overflow reports from BalanceSheet / ProfitLoss. */
  const [tableOverflowById, setTableOverflowById] = React.useState<Record<string, boolean>>({});
  const headerRightSlotRef = React.useRef<HTMLDivElement | null>(null);
  const headerRightContentRef = React.useRef<HTMLDivElement | null>(null);
  const initialHeaderRightWidthRef = React.useRef<number | null>(null);
  /** Whether left pane is currently narrow — used to normalize table overflow (avoid width feedback loop). */
  const leftPaneNarrowRef = React.useRef(false);
  const minWidth = LEFT_PANE_ICON_WIDTH;
  const leftWidth = LEFT_PANE_FULL_WIDTH;
  const hasHeaderRight = headerRight != null && headerRight !== false;
  const hasTableReporters = Object.keys(tableOverflowById).length > 0;
  const anyTableOverflow = Object.values(tableOverflowById).some(Boolean);
  // Q1:A — BS/PL reporters present → table overflow wins over headerRight
  const compactByWidth = hasTableReporters ? anyTableOverflow : compactByHeaderRight;
  // Auto icon/headerLeft shrink; manual expand overrides to full names.
  const autoCompactUi = compactByWidth && !forceExpanded;
  const iconMode = collapsed || autoCompactUi;
  const headerLeftCompact = iconMode;
  // Shrink left pane width together with icon / headerLeft compact mode.
  const effectiveLeftWidth = iconMode ? minWidth : leftWidth;
  leftPaneNarrowRef.current = effectiveLeftWidth === minWidth;

  const currentKey = selectedKey ?? innerKey;

  const tableOverflowApi = React.useMemo<SplitTabsTableOverflowApi>(() => ({
    report: (id, el) => {
      const clientWidth = el.clientWidth;
      // display:none → clientWidth 0; inactive tabs must not force icon mode
      if (clientWidth <= 0) {
        setTableOverflowById((prev) => (prev[id] === false ? prev : { ...prev, [id]: false }));
        return;
      }
      const scrollWidth = el.scrollWidth;
      // Normalize to "would overflow if left pane were full width" so narrowing left does not clear overflow and bounce.
      const leftDelta = LEFT_PANE_FULL_WIDTH - LEFT_PANE_ICON_WIDTH;
      const clientAtFullLeft = leftPaneNarrowRef.current
        ? Math.max(0, clientWidth - leftDelta)
        : clientWidth;
      const overflow = scrollWidth > clientAtFullLeft;
      setTableOverflowById((prev) => (prev[id] === overflow ? prev : { ...prev, [id]: overflow }));
    },
    unregister: (id) => {
      setTableOverflowById((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
  }), []);

  React.useEffect(() => {
    if (!tabs.find(t => t.key === innerKey) && tabs.length > 0) {
      setInnerKey(tabs[0].key);
    }
  }, [tabs, innerKey]);

  // Overflow gone → drop manual expand override so next overflow can auto-compact again.
  React.useEffect(() => {
    if (!compactByWidth) setForceExpanded(false);
  }, [compactByWidth]);

  const handleToggleCollapsed = () => {
    if (collapsed || autoCompactUi) {
      // Manual expand: full-name tabs + full headerLeft + full left width even if table still overflows.
      setCollapsed(false);
      setForceExpanded(true);
      return;
    }
    setForceExpanded(false);
    setCollapsed(true);
  };

  React.useEffect(() => {
    if (!hasHeaderRight || typeof ResizeObserver === 'undefined') {
      initialHeaderRightWidthRef.current = null;
      setCompactByHeaderRight(false);
      return;
    }

    const applyWidth = () => {
      const contentEl = headerRightContentRef.current;
      const slotEl = headerRightSlotRef.current;
      if (!contentEl || !slotEl) return;
      // Intrinsic width of headerRight content (does not shrink with flex).
      const contentWidth = contentEl.scrollWidth;
      if (contentWidth <= 0) return;
      // Lock once on first stable layout of headerRight content.
      if (initialHeaderRightWidthRef.current == null) {
        initialHeaderRightWidthRef.current = contentWidth;
      }
      const initial = initialHeaderRightWidthRef.current;
      const current = slotEl.getBoundingClientRect().width;
      // initial > current slot → icon labels; else full names
      setCompactByHeaderRight(initial > current);
    };

    applyWidth();
    const ro = new ResizeObserver(() => applyWidth());
    if (headerRightContentRef.current) ro.observe(headerRightContentRef.current);
    if (headerRightSlotRef.current) ro.observe(headerRightSlotRef.current);
    return () => ro.disconnect();
  }, [hasHeaderRight]);

  const handleChange = (_: React.SyntheticEvent, value: string) => {
    if (onChange) onChange(value);
    setInnerKey(value);
  };

  const current = tabs.find(t => t.key === currentKey);

  const collapsedLabelFor = (t: SplitTabItem): React.ReactNode => {
    if (t.icon != null) return t.icon;
    if (typeof t.label === 'string' && t.label.trim()) return t.label;
    // ReactNode labels (e.g. DirectoryTabLabel) — fall back to tab key (A / B / C8.2)
    return t.key;
  };

  return (
    <SplitTabsTableOverflowContext.Provider value={tableOverflowApi}>
      <SplitTabsHeaderCompactContext.Provider value={headerLeftCompact}>
      <Stack spacing={1} sx={{ width: '100%', height: maximized ? '100vh' : height, ...(sx as any) }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${effectiveLeftWidth}px minmax(0, 1fr)`,
            gap: 1,
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            position: maximized ? 'fixed' : 'relative',
            inset: maximized ? 0 : 'auto',
            zIndex: maximized ? (theme => theme.zIndex.modal + 1) : 'auto',
            bgcolor: maximized ? (theme => theme.palette.background.paper) : 'transparent',
            width: maximized ? '100vw' : 'auto',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRight: theme => `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              width: effectiveLeftWidth,
              minWidth: 0,
              maxWidth: effectiveLeftWidth,
              ...(leftPaneSx as any),
              // iconMode (auto or manual) must pin width against caller leftPaneSx (e.g. minWidth:180)
              ...(iconMode
                ? { minWidth: effectiveLeftWidth, maxWidth: effectiveLeftWidth, width: effectiveLeftWidth }
                : null),
            }}
          >
            <Stack direction="column" sx={{ p: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: iconMode ? 'center' : 'flex-end' }}>
                <IconButton
                  size="small"
                  aria-label={iconMode ? 'Expand tabs' : 'Collapse tabs'}
                  onClick={handleToggleCollapsed}
                >
                  {iconMode ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Stack>
            <Tabs
              orientation="vertical"
              value={currentKey}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons={iconMode ? false : 'auto'}
              sx={{
                height: `calc(100% - 32px)`,
                width: '100%',
                minWidth: 0,
                '& .MuiTabs-flexContainer': {
                  alignItems: iconMode ? 'stretch' : 'flex-start',
                },
                '& .MuiTabs-indicator': {
                  left: 0,
                  right: 'auto',
                  width: 3,
                },
                '& .MuiTabs-scrollButtons': { display: iconMode ? 'none' : undefined },
              }}
            >
              {tabs.map(t => {
                const tip = typeof t.label === 'string' ? t.label : t.key;
                // disabled 时用原生 title（勿包 Tooltip，否则 Tabs 直系 Tab 约定被破坏）
                const titleText = t.disabled && t.disabledTooltip
                  ? t.disabledTooltip
                  : (iconMode ? tip : undefined);
                return (
                  <Tab
                    key={t.key}
                    label={iconMode ? collapsedLabelFor(t) : t.label}
                    value={t.key}
                    disabled={!!t.disabled}
                    title={titleText}
                    sx={{
                      alignItems: iconMode ? 'center' : 'flex-start',
                      justifyContent: 'center',
                      minHeight: 40,
                      minWidth: 0,
                      maxWidth: '100%',
                      width: '100%',
                      px: iconMode ? 0.25 : 1,
                      py: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: iconMode ? 'nowrap' : 'normal',
                      fontSize: iconMode ? '0.7rem' : undefined,
                      fontWeight: iconMode ? 700 : undefined,
                      lineHeight: 1.2,
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          <Box
            sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, minWidth: 0, ...(rightPaneSx as any) }}
          >
            {(headerLeft || hasHeaderRight || allowMaximize || errorMsg || warningMsg) && (
              <Box sx={{position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'background.paper', p: 1}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {headerLeft}
                    {errorMsg && (
                      <Alert variant="filled" severity="error" sx={{ width: '65%', whiteSpace: 'pre-line' }}>{errorMsg}</Alert>
                    )}
                    {warningMsg && (
                      <Alert variant="filled" severity="warning" sx={{ width: '65%', whiteSpace: 'pre-line' }}>{warningMsg}</Alert>
                    )}
                  </Box>
                  {hasHeaderRight ? (
                    <Box
                      ref={headerRightSlotRef}
                      sx={{ minWidth: 0, flexShrink: 1, overflow: 'hidden', display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <Box ref={headerRightContentRef} sx={{ flexShrink: 0, display: 'inline-flex' }}>
                        {headerRight}
                      </Box>
                    </Box>
                  ) : null}
                  {allowMaximize && (
                    <IconButton size="small" onClick={() => setMaximized(m => !m)}>
                      {maximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                  )}
                </Stack>
              </Box>
            )}
            <Divider />
            <Box sx={{
              flex: 1,
              minHeight: 0,
              overflow: preserveInactivePaneLayout && renderAll ? 'hidden' : 'auto',
              p: preserveInactivePaneLayout && renderAll ? 0 : 1,
              position: preserveInactivePaneLayout && renderAll ? 'relative' : undefined,
              ...(contentWrapperSx as any),
            }}>
              {renderAll ? (
                tabs && tabs.length ? (
                  tabs.map(t => (
                    <Box
                      key={t.key}
                      sx={preserveInactivePaneLayout
                          ? {
                              position: 'absolute',
                              inset: 0,
                              overflow: 'auto',
                              p: 1,
                              visibility: t.key === currentKey ? 'visible' : 'hidden',
                              pointerEvents: t.key === currentKey ? 'auto' : 'none',
                          }
                          : {
                              display: t.key === currentKey ? 'block' : 'none',
                              height: '100%',
                          }}
                    >
                      {t.content}
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">No tab selected</Typography>
                  </Box>
                )
              ) : (
                current ? (
                  current.content
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">No tab selected</Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Stack>
      </SplitTabsHeaderCompactContext.Provider>
    </SplitTabsTableOverflowContext.Provider>
  );
}
